// Practice-scoped state for the Integrations "Phone system" card. Mirrors the
// Cal.com slice: every thunk bumps a generation counter and discards a response
// that completes after a newer request, and every reducer no-ops when the
// payload's practiceId differs from the one the state belongs to, so a practice
// switch mid-request can never paint practice A's connections on practice B.
//
// The signing secret returned by create/rotate is kept ONLY in `reveal` and is
// cleared by `clearSecretReveal()` when the user closes the dialog.
import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';
import { updateError } from './user';
import { VOICE_URLS, FALLBACK_PROVIDERS, httpsWebhookUrl } from '../../pages/dashboard/practices/voice/voiceConnections.api';

const initialState = {
    practiceId: null,
    items: [],
    providers: FALLBACK_PROVIDERS,
    loading: false,
    saving: false,
    error: null,
    // { connectionId, secret, webhookUrl, reason: 'created' | 'rotated' }
    reveal: null,
    // keyed by connection id → { data, loading, error }
    status: {},
    // keyed by connection id → { rows, loading, saving, error }
    userMapping: {},
    // last test-event result keyed by connection id → { event_id, call_id, accepted, at }
    testEvents: {},
    // connection ids whose secret was rotated but the re-enable PATCH failed;
    // the card offers "Retry enable" which must NOT rotate again.
    pendingEnable: {},
};

const sameScope = (state, practiceId) => String(state.practiceId) === String(practiceId);

const slice = createSlice({
    name: 'voiceConnections',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { practiceId, loading } = action.payload;
            if (!sameScope(state, practiceId)) {
                state.items = [];
                state.status = {};
                state.userMapping = {};
                state.testEvents = {};
                state.pendingEnable = {};
                state.reveal = null;
                state.saving = false;
            }
            state.practiceId = practiceId;
            state.loading = loading;
            state.error = null;
        },
        setItems(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            state.items = action.payload.items;
            state.loading = false;
            state.error = null;
        },
        setProviders(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            if (Array.isArray(action.payload.providers) && action.payload.providers.length) {
                state.providers = action.payload.providers;
            }
        },
        setSaving(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            state.saving = action.payload.saving;
            if (action.payload.saving) state.error = null;
        },
        setError(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            state.error = action.payload.error;
            state.loading = false;
            state.saving = false;
        },
        upsertItem(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            const row = action.payload.item;
            const idx = state.items.findIndex((it) => String(it.id) === String(row.id));
            if (idx === -1) state.items.push(row); else state.items[idx] = { ...state.items[idx], ...row };
            state.saving = false;
        },
        setReveal(state, action) {
            if (!sameScope(state, action.payload.practiceId)) return;
            state.reveal = action.payload.reveal;
        },
        clearSecretReveal(state) {
            state.reveal = null;
        },
        setStatus(state, action) {
            const { practiceId, connectionId, patch } = action.payload;
            if (!sameScope(state, practiceId)) return;
            state.status[connectionId] = { ...(state.status[connectionId] || {}), ...patch };
        },
        setUserMapping(state, action) {
            const { practiceId, connectionId, patch } = action.payload;
            if (!sameScope(state, practiceId)) return;
            state.userMapping[connectionId] = { ...(state.userMapping[connectionId] || {}), ...patch };
        },
        setPendingEnable(state, action) {
            const { practiceId, connectionId, pending } = action.payload;
            if (!sameScope(state, practiceId)) return;
            if (pending) state.pendingEnable[connectionId] = true; else delete state.pendingEnable[connectionId];
        },
        setTestEvent(state, action) {
            const { practiceId, connectionId, result } = action.payload;
            if (!sameScope(state, practiceId)) return;
            state.testEvents[connectionId] = result;
        },
    },
});

export default slice.reducer;
export const { clearSecretReveal } = slice.actions;

// Only LIST fetches are sequenced: a newer list request discards an older
// list response. Mutations are never discarded by a list refresh — their
// reducers already no-op when the store has moved to another practice, and
// discarding a successful create/rotate would lose a one-time secret
// (staging RCA 2026-10-11, item 2).
let listGeneration = 0;
const nextListGeneration = () => { listGeneration += 1; return listGeneration; };
const staleList = (g) => g !== listGeneration;

const errorMessage = (response, fallback) => (
    response?.data?.detail
    || response?.data?.error
    || response?.data?.non_field_errors?.[0]
    || (response?.data && typeof response.data === 'object'
        ? Object.values(response.data).flat().find((v) => typeof v === 'string')
        : null)
    || fallback
);

// The user-mapping PUT is a many=True serializer, so a refused row comes back as a
// list with one dict per row (empty for the rows that were fine). errorMessage only
// reads dicts and fell back to "Failed to save user mapping" whatever the cause, so
// name the row the server refused and give its reason.
const rowErrorMessage = (response, rows, fallback) => {
    if (!Array.isArray(response?.data)) return errorMessage(response, fallback);
    for (let i = 0; i < response.data.length; i += 1) {
        const entry = response.data[i];
        const reason = typeof entry === 'string'
            ? entry
            : Object.values(entry || {}).flat().find((v) => typeof v === 'string');
        if (reason) return `${rows?.[i]?.vendor_label || `Row ${i + 1}`}: ${reason}`;
    }
    return fallback;
};

// Secret-bearing responses never go through `updateError`/Sentry: only the
// row (without `secret`) is stored on the list.
const stripSecret = (row) => {
    if (!row || typeof row !== 'object') return row;
    const rest = { ...row };
    delete rest.secret;
    return rest;
};

export function getVoiceConnections(practiceId) {
    return async () => {
        const g = nextListGeneration();
        dispatch(slice.actions.setLoading({ practiceId, loading: true }));
        try {
            const [list, providers] = await Promise.all([
                callApi(VOICE_URLS.list(practiceId), 'GET', null, false, { bustCache: true }),
                callApi(VOICE_URLS.providers(practiceId)),
            ]);
            if (staleList(g)) return;
            if (list.status === 200) {
                const items = Array.isArray(list.data) ? list.data : (list.data?.results || []);
                dispatch(slice.actions.setItems({ practiceId, items }));
            } else if (list.status === 404) {
                dispatch(slice.actions.setItems({ practiceId, items: [] }));
            } else {
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(list, 'Failed to load phone system connections') }));
            }
            if (providers.status === 200) {
                const rows = Array.isArray(providers.data) ? providers.data : (providers.data?.results || []);
                dispatch(slice.actions.setProviders({ practiceId, providers: rows }));
            }
        } catch (err) {
            if (staleList(g)) return;
            dispatch(slice.actions.setError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}

export function createVoiceConnection(practiceId, data, onSuccess, onError) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const response = await callApi(VOICE_URLS.list(practiceId), 'POST', data);
            if (response.status === 201 || response.status === 200) {
                const row = response.data || {};
                dispatch(slice.actions.upsertItem({ practiceId, item: stripSecret(row) }));
                // Subscription providers (Elevate) return no secret: the CRM
                // subscribes in the background, so there is nothing to reveal.
                if (row.secret) {
                    dispatch(slice.actions.setReveal({
                        practiceId,
                        reveal: { connectionId: row.id, secret: row.secret, webhookUrl: httpsWebhookUrl(row.webhook_url), reason: 'created' },
                    }));
                } else {
                    dispatch(updateError({ success: true, message: 'Phone system connected. Subscribing to call events…' }));
                }
                if (onSuccess) onSuccess(stripSecret(row));
            } else {
                const msg = errorMessage(response, 'Failed to connect the phone system');
                dispatch(slice.actions.setError({ practiceId, error: msg }));
                if (onError) onError(msg);
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setError({ practiceId, error: msg }));
            if (onError) onError(msg);
        }
    };
}

export function updateVoiceConnection(practiceId, connectionId, data, onSuccess) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const response = await callApi(VOICE_URLS.detail(practiceId, connectionId), 'PATCH', data);
            if (response.status === 200) {
                dispatch(slice.actions.upsertItem({ practiceId, item: stripSecret(response.data) }));
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(response, 'Failed to save the connection') }));
            }
        } catch (err) {
            dispatch(slice.actions.setError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}

export function rotateVoiceConnectionSecret(practiceId, connectionId) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const response = await callApi(VOICE_URLS.rotateSecret(practiceId, connectionId), 'POST', {});
            if (response.status === 200 || response.status === 201) {
                if (response.data?.secret) {
                    dispatch(slice.actions.upsertItem({
                        practiceId,
                        item: { id: connectionId, secret_rotated_at: response.data?.secret_rotated_at || new Date().toISOString() },
                    }));
                    dispatch(slice.actions.setReveal({
                        practiceId,
                        reveal: { connectionId, secret: response.data?.secret, webhookUrl: httpsWebhookUrl(response.data?.webhook_url), reason: 'rotated' },
                    }));
                } else {
                    // Subscription provider: the backend rotated the delivery
                    // credentials itself and is replacing the subscription.
                    dispatch(slice.actions.upsertItem({ practiceId, item: stripSecret({ ...(response.data || {}), id: connectionId }) }));
                    dispatch(updateError({ success: true, message: 'Delivery credentials rotated. The subscription is being replaced.' }));
                }
            } else {
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(response, 'Failed to rotate the secret') }));
            }
        } catch (err) {
            dispatch(slice.actions.setError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}

export function disconnectVoiceConnection(practiceId, connectionId, onSuccess) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const response = await callApi(VOICE_URLS.disconnect(practiceId, connectionId), 'POST', {});
            if (response.status === 200 || response.status === 204) {
                const row = response.status === 200 && response.data ? stripSecret(response.data) : { id: connectionId, is_enabled: false, status: 'disabled' };
                dispatch(slice.actions.upsertItem({ practiceId, item: { ...row, id: connectionId, is_enabled: false } }));
                dispatch(updateError({ success: true, message: 'Phone system disconnected' }));
                if (onSuccess) onSuccess();
            } else {
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(response, 'Failed to disconnect the phone system') }));
            }
        } catch (err) {
            dispatch(slice.actions.setError({ practiceId, error: err.message || 'Something went wrong' }));
        }
    };
}

// A disconnected connection can only be re-enabled once it holds a signing
// secret (backend validation), so reconnect = rotate (new secret, shown once)
// then PATCH is_enabled. The vendor must switch to the new secret.
export function reconnectVoiceConnection(practiceId, connectionId) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const rotated = await callApi(VOICE_URLS.rotateSecret(practiceId, connectionId), 'POST', {});
            if (rotated.status !== 200 && rotated.status !== 201) {
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(rotated, 'Failed to issue a new secret') }));
                return;
            }
            // The server has already changed the secret: reveal it NOW, before
            // anything else can fail, so it is never lost (RCA item 3).
            dispatch(slice.actions.upsertItem({
                practiceId,
                item: { id: connectionId, secret_rotated_at: rotated.data?.secret_rotated_at || new Date().toISOString() },
            }));
            dispatch(slice.actions.setReveal({
                practiceId,
                reveal: { connectionId, secret: rotated.data?.secret, webhookUrl: httpsWebhookUrl(rotated.data?.webhook_url), reason: 'reconnected' },
            }));
        } catch (err) {
            dispatch(slice.actions.setError({ practiceId, error: err.message || 'Something went wrong' }));
            return;
        }
        await enableVoiceConnection(practiceId, connectionId)();
    };
}

// Subscription providers: a disconnected connection is reconnected with a new
// client id/secret. The backend validates them against the vendor, re-enables
// the connection and schedules a fresh subscription. It refuses (400) while
// the previous subscription is still being cleaned up.
export function reconnectVoiceConnectionWithCredentials(practiceId, connectionId, credentials, onSuccess, onError) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const response = await callApi(VOICE_URLS.reconnect(practiceId, connectionId), 'POST', credentials);
            if (response.status === 200) {
                dispatch(slice.actions.upsertItem({ practiceId, item: { ...stripSecret(response.data), id: connectionId } }));
                dispatch(updateError({ success: true, message: 'Credentials accepted. Subscribing to call events…' }));
                if (onSuccess) onSuccess(response.data);
            } else {
                const msg = errorMessage(response, 'Failed to reconnect the phone system');
                dispatch(slice.actions.setError({ practiceId, error: msg }));
                if (onError) onError(msg);
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setError({ practiceId, error: msg }));
            if (onError) onError(msg);
        }
    };
}

// Re-enable without rotating. Used by reconnect after its rotate step and by
// the card's "Retry enable" when that step failed or timed out. For
// subscription providers this is also "Retry subscription": PATCH is_enabled
// re-runs the subscribe task with the stored credentials.
export function enableVoiceConnection(practiceId, connectionId) {
    return async () => {
        dispatch(slice.actions.setSaving({ practiceId, saving: true }));
        try {
            const enabled = await callApi(VOICE_URLS.detail(practiceId, connectionId), 'PATCH', { is_enabled: true });
            if (enabled.status === 200) {
                dispatch(slice.actions.upsertItem({ practiceId, item: stripSecret(enabled.data) }));
                dispatch(slice.actions.setPendingEnable({ practiceId, connectionId, pending: false }));
            } else {
                dispatch(slice.actions.setPendingEnable({ practiceId, connectionId, pending: true }));
                dispatch(slice.actions.setError({ practiceId, error: errorMessage(enabled, 'The new secret was issued but the connection could not be re-enabled. Use Retry enable; it will not change the secret again.') }));
            }
        } catch (err) {
            dispatch(slice.actions.setPendingEnable({ practiceId, connectionId, pending: true }));
            dispatch(slice.actions.setError({ practiceId, error: `${err.message || 'Something went wrong'}. The new secret was issued; use Retry enable, it will not change the secret again.` }));
        }
    };
}

// Per-connection fetches below do not bump the list generation: a status
// panel refreshing must not cancel an in-flight create.
export function getVoiceConnectionStatus(practiceId, connectionId, hours = 24) {
    return async () => {
        dispatch(slice.actions.setStatus({ practiceId, connectionId, patch: { loading: true, error: null } }));
        try {
            const response = await callApi(VOICE_URLS.status(practiceId, connectionId, hours), 'GET', null, false, { bustCache: true });
            if (response.status === 200) {
                dispatch(slice.actions.setStatus({ practiceId, connectionId, patch: { loading: false, data: response.data, hours } }));
            } else {
                dispatch(slice.actions.setStatus({ practiceId, connectionId, patch: { loading: false, error: errorMessage(response, 'Failed to load status') } }));
            }
        } catch (err) {
            dispatch(slice.actions.setStatus({ practiceId, connectionId, patch: { loading: false, error: err.message || 'Something went wrong' } }));
        }
    };
}

export function sendVoiceTestEvent(practiceId, connectionId, onDone) {
    return async () => {
        dispatch(slice.actions.setTestEvent({ practiceId, connectionId, result: { pending: true } }));
        try {
            const response = await callApi(VOICE_URLS.testEvent(practiceId, connectionId), 'POST', {});
            const ok = response.status === 200 || response.status === 201 || response.status === 202;
            const result = ok
                ? { ...(response.data || {}), accepted: response.data?.accepted !== false, at: new Date().toISOString() }
                : { accepted: false, error: errorMessage(response, 'The test event was not accepted'), at: new Date().toISOString() };
            dispatch(slice.actions.setTestEvent({ practiceId, connectionId, result }));
            if (onDone) onDone(result);
        } catch (err) {
            dispatch(slice.actions.setTestEvent({ practiceId, connectionId, result: { accepted: false, error: err.message || 'Something went wrong', at: new Date().toISOString() } }));
        }
    };
}

export function getVoiceUserMapping(practiceId, connectionId) {
    return async () => {
        dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { loading: true, error: null } }));
        try {
            const response = await callApi(VOICE_URLS.userMapping(practiceId, connectionId), 'GET', null, false, { bustCache: true });
            if (response.status === 200) {
                const rows = Array.isArray(response.data) ? response.data : (response.data?.results || []);
                dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { loading: false, rows } }));
            } else {
                dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { loading: false, error: errorMessage(response, 'Failed to load user mapping') } }));
            }
        } catch (err) {
            dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { loading: false, error: err.message || 'Something went wrong' } }));
        }
    };
}

export function saveVoiceUserMapping(practiceId, connectionId, rows, onSuccess) {
    return async () => {
        dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { saving: true, error: null } }));
        try {
            const response = await callApi(VOICE_URLS.userMapping(practiceId, connectionId), 'PUT', rows);
            if (response.status === 200) {
                const saved = Array.isArray(response.data) ? response.data : rows;
                dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { saving: false, rows: saved } }));
                dispatch(updateError({ success: true, message: 'User mapping saved' }));
                if (onSuccess) onSuccess(saved);
                return { ok: true };
            }
            // Callers that write one row at a time need to know a save failed, not just
            // that it never succeeded: without an answer they wait forever.
            const message = rowErrorMessage(response, rows, 'Failed to save user mapping');
            dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { saving: false, error: message } }));
            return { ok: false, message };
        } catch (err) {
            const message = err.message || 'Something went wrong';
            dispatch(slice.actions.setUserMapping({ practiceId, connectionId, patch: { saving: false, error: message } }));
            return { ok: false, message };
        }
    };
}
